import cx from 'classnames';
import { InferGetServerSidePropsType } from 'next';
import { useIntl } from 'react-intl';
import { BreadcrumbList, Person, WebPage, WithContext } from 'schema-dts';

import { Locale } from '../../../../../core/i18n/Locale';
import { Site } from '../../../../../core/Site/Site';
import { useTrackPageViewAmplitude } from '../../../../../core/Tracking/Amplitude/Amplitude';
import { BreadcrumbItem } from '../../../../../design-system/components/breadcrumb/Breadcrumb';
import {
    Breadcrumb,
    useDestinationInspirationArticleBreadcrumbItems,
} from '../../../../../domains/Layout/Breadcrumb/Breadcrumb';
import { Footer } from '../../../../../domains/Layout/Footer/Footer';
import { Head } from '../../../../../domains/Layout/Head/Head';
import { useJsonLd } from '../../../../../domains/Layout/Head/JsonLd';
import { getAuthorJsonLd } from '../../../../../domains/Layout/Head/utilities/schemaGenerators';
import { imageLoaderForId } from '../../../../../domains/Layout/Image/URL/ImageURL';
import { PrimaryNav } from '../../../../../domains/Layout/PrimaryNav/PrimaryNav';
import { DestinationSuitcaseInspirationLink } from '../../../../../domains/TravelGuide/Article/DestinationSuitcaseInspirationLink';
import { DestinationVocabularyInspirationLink } from '../../../../../domains/TravelGuide/Article/DestinationVocabularyInspirationLink';
import { DestinationInspirationArticle } from '../../../../../partials/destination-inspiration-article/Article/DestinationInspirationArticle';
import { getServerSideProps } from '../../../../../partials/destination-inspiration-article/getServerSideProps';
import { Author } from '../../../../../resources/author/domain/types/author';
import { Destination } from '../../../../../resources/destination/domain/types/destination';
import { DestinationArticle } from '../../../../../resources/destinationArticles/domain/types/destinationArticle';
import { AuthorBannerSection } from '../../../../../shared/components/AuthorBannerSection/AuthorBannerSection';
import { DestinationArticlesCarouselSection } from '../../../../../shared/components/DestinationArticlesCarouselSection/DestinationArticlesCarouselSection';
import { ItineraryListCarouselSection } from '../../../../../shared/components/ItineraryListCarouselSection/ItineraryListCarouselSection';
import { useLocale } from '../../../../../shared/hooks/useLocale';
import { useUrlGenerator } from '../../../../../shared/hooks/useUrlGenerator';
import { useCurrentSite } from '../../../../../shared/providers/currentSite/useCurrentSite';
import { htmlToText } from '../../../../../shared/utilities/htmlToText';
import { generateUrl } from '../../../../../shared/utilities/routing/routing';
import { PickDomain } from '../../../../../shared/utilities/types/PickDomain';

import style from './index.module.css';

export { getServerSideProps };

type PageDestinationInspirationArticleProps = InferGetServerSidePropsType<
    typeof getServerSideProps
>;

export default function PageDestinationInspirationArticle(
    props: PageDestinationInspirationArticleProps,
) {
    const { currentSite } = useCurrentSite();
    const { generateUrl } = useUrlGenerator();
    const { locale } = useLocale();
    const { formatMessage } = useIntl();

    const {
        destinationInspirationArticle,
        itineraries,
        destinationItinerariesCount,
        otherDestinationInspirationArticles,
        hasDestinationSuitcase,
        hasDestinationVocabulary,
        author,
        primaryNav,
        hrefLangs,
    } = props.data;

    const { destination } = destinationInspirationArticle;

    const { breadcrumbItems } = useDestinationInspirationArticleBreadcrumbItems({
        destination,
        destinationInspirationArticle,
    });

    const destinationInspirationArticleUrl = generateUrl('destination_inspiration_article', {
        destinationSlug: destination.slug,
        articleDocumentId: destinationInspirationArticle.documentId,
        articleSlug: destinationInspirationArticle.slug,
    });

    const metaTitle = formatMessage(
        {
            id: 'page.destinationInspiration.meta.title',
        },
        {
            destinationName: destination.name,
            title: destinationInspirationArticle.shortTitle,
        },
    );

    const metaDescription = htmlToText(destinationInspirationArticle.summary);

    const meta = getMetaData({
        destination,
        locale,
        author,
        destinationInspirationArticle,
        metaTitle,
        metaDescription,
        currentSite,
        breadcrumbItems,
    });

    useTrackPageViewAmplitude((ampli, defaultProperties) =>
        ampli.viewedPageDestinationArticle({
            ...defaultProperties,
            destination_id: destination.id,
            destination_slug: destination.slug,
            has_trip_form_available: destination.isAvailableForQuoteRequest,
            article_type: destinationInspirationArticle.type,
        }),
    );

    return (
        <>
            <Head
                title={metaTitle}
                description={metaDescription}
                canonical={destinationInspirationArticleUrl}
                hrefLangs={hrefLangs}
                og={{
                    title: metaTitle,
                    description: metaDescription,
                    image: imageLoaderForId({
                        aspectRatio: 1.91,
                    })({
                        src: destination.coverPicture.id.toString(),
                        width: 1200,
                    }),
                    locale,
                    url: destinationInspirationArticleUrl,
                    type: 'article',
                    site_name: currentSite.organisation.name,
                }}
                jsonLd={useJsonLd(meta.jsonLd)}
            />

            <PrimaryNav contextContinent={destination.continent} {...primaryNav} />

            <div className={cx('layout-with-centered-content', style.breadcrumbSpacing)}>
                <section className="main-container">
                    <Breadcrumb items={breadcrumbItems} />
                </section>
            </div>

            <div className={cx('layout-with-centered-content', style.sectionSpacingResponsive)}>
                <section className="main-container">
                    <DestinationInspirationArticle
                        article={destinationInspirationArticle}
                        destination={destination}
                        author={author}
                    />
                </section>
            </div>

            {author ? (
                <div className={cx('layout-with-centered-content', style.sectionSpacingResponsive)}>
                    <section className="main-container">
                        <AuthorBannerSection author={author} />
                    </section>
                </div>
            ) : null}

            {itineraries.length > 0 ? (
                <div className={cx('layout-with-centered-content', style.sectionSpacingResponsive)}>
                    <section className="main-container">
                        <ItineraryListCarouselSection
                            destination={destination}
                            itineraries={itineraries}
                            destinationItinerariesCount={destinationItinerariesCount}
                            itineraryCardTitleElement="div"
                        />
                    </section>
                </div>
            ) : null}

            {otherDestinationInspirationArticles.length > 0 ? (
                <div className={cx('layout-with-centered-content', style.sectionSpacingResponsive)}>
                    <section className="main-container">
                        <DestinationArticlesCarouselSection
                            destination={destination}
                            articles={otherDestinationInspirationArticles}
                        />
                    </section>
                </div>
            ) : null}

            {hasDestinationSuitcase || hasDestinationVocabulary ? (
                <div className={cx('layout-with-centered-content', style.sectionSpacingResponsive)}>
                    <section className="main-container">
                        <div className="row">
                            {hasDestinationSuitcase ? (
                                <div className={cx('col-12 col-lg-6', style.colSpacing)}>
                                    <DestinationSuitcaseInspirationLink destination={destination} />
                                </div>
                            ) : null}
                            {hasDestinationVocabulary ? (
                                <div className="col-12 col-lg-6">
                                    <DestinationVocabularyInspirationLink
                                        destination={destination}
                                    />
                                </div>
                            ) : null}
                        </div>
                    </section>
                </div>
            ) : null}

            <Footer
                destination={destination}
                topDestinations={primaryNav.destinationsMenu?.world.topDestinations ?? []}
            />
        </>
    );
}

function getMetaData({
    destination,
    locale,
    author,
    metaTitle,
    metaDescription,
    currentSite,
    destinationInspirationArticle,
    breadcrumbItems,
}: {
    destination: PickDomain<Destination, 'name' | 'slug' | 'coverPicture' | 'preposition'>;
    locale: Locale;
    author: PickDomain<
        Author,
        'id' | 'firstName' | 'lastName' | 'role' | 'description' | 'picture' | 'slug',
        {
            linkedinUrl: Author['linkedinUrl'] | null;
            instagramUrl: Author['instagramUrl'] | null;
        }
    > | null;
    metaTitle: string;
    metaDescription: string;
    currentSite: Site;
    destinationInspirationArticle: PickDomain<
        DestinationArticle,
        'content' | 'updatedAt' | 'documentId' | 'slug' | 'title' | 'publishedAt'
    >;
    breadcrumbItems: Array<BreadcrumbItem>;
}) {
    const headline = destinationInspirationArticle.title;

    const webPageJsonLd = getWebPageJsonLd({
        locale,
        destination,
        metaDescription,
        currentSite,
        headline,
        destinationInspirationArticle,
        author,
        breadcrumbItems,
    });

    return {
        title: metaTitle,
        description: metaDescription,
        jsonLd: [webPageJsonLd],
    };
}

function getWebPageJsonLd({
    locale,
    destination,
    metaDescription,
    currentSite,
    headline,
    destinationInspirationArticle,
    author,
    breadcrumbItems,
}: {
    locale: Locale;
    destination: PickDomain<Destination, 'name' | 'slug' | 'coverPicture'>;
    metaDescription: string;
    headline: string;
    currentSite: Site;
    destinationInspirationArticle: PickDomain<
        DestinationArticle,
        'content' | 'updatedAt' | 'documentId' | 'slug' | 'publishedAt'
    >;
    author: PickDomain<
        Author,
        'id' | 'firstName' | 'lastName' | 'role' | 'description' | 'picture' | 'slug',
        {
            linkedinUrl: Author['linkedinUrl'] | null;
            instagramUrl: Author['instagramUrl'] | null;
        }
    > | null;
    breadcrumbItems: Array<BreadcrumbItem>;
}): WithContext<WebPage> | null {
    const destinationInspirationArticlePageUrl = generateUrl(
        'destination_inspiration_article',
        locale,
        {
            destinationSlug: destination.slug,
            articleDocumentId: destinationInspirationArticle.documentId,
            articleSlug: destinationInspirationArticle.slug,
        },
    );
    const articleIdUrl = `${destinationInspirationArticlePageUrl}#article`;

    const authorEntity: WithContext<Person> | null = author
        ? getAuthorJsonLd({
              author,
              locale,
              currentSite,
          })
        : null;

    const mainEntity: WithContext<WebPage>['mainEntity'] = {
        '@type': 'Article',
        '@id': articleIdUrl,
        headline,
        url: destinationInspirationArticlePageUrl,
        articleBody: htmlToText(destinationInspirationArticle.content),
        mainEntityOfPage: destinationInspirationArticlePageUrl,
        ...(authorEntity ? { author: authorEntity } : {}),
    };

    const breadcrumbList: BreadcrumbList = {
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbItems.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.text,
            item: item.url,
        })),
    };

    return {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${destinationInspirationArticlePageUrl}#webpage`,
        url: destinationInspirationArticlePageUrl,
        inLanguage: locale,
        headline,
        description: metaDescription,
        mainEntity,
        breadcrumb: breadcrumbList,
        ...(destinationInspirationArticle.updatedAt
            ? { dateModified: destinationInspirationArticle.updatedAt }
            : {}),
        ...(destinationInspirationArticle.publishedAt
            ? { datePublished: destinationInspirationArticle.publishedAt }
            : {}),
    };
}
